/**
 * The words on screen. Catalogs are plain objects per language — the platform's and each domain's,
 * merged (a domain key overrides a platform key). `{name}` is replaced by params, every time it appears.
 *
 * A missing key is never hidden: the screen shows ⟦key⟧ and the diagnostics say which key, in which
 * language. A key missing only in the current language falls back to Italian and is reported too.
 */
import { reportOnce } from '../core/diagnostics';

export type Catalog = Record<string, string>;
export type Catalogs = Partial<Record<string, Catalog>>;
export type Params = Record<string, string | number>;
export type Translate = (key: string, params?: Params) => string;

export const FALLBACK_LANGUAGE = 'it';

export function mergeCatalogs(...sets: Catalogs[]): Catalogs {
  const out: Catalogs = {};
  for (const set of sets) {
    for (const [lang, catalog] of Object.entries(set)) {
      out[lang] = { ...(out[lang] ?? {}), ...(catalog ?? {}) };
    }
  }
  return out;
}

export function createTranslator(catalogs: Catalogs, language: string): Translate {
  const own = catalogs[language] ?? {};
  const fallback = catalogs[FALLBACK_LANGUAGE] ?? {};
  return (key, params) => {
    let text = own[key];
    if (text === undefined) {
      text = fallback[key];
      if (text === undefined) {
        reportOnce('warn', 'i18n', `missing key "${key}"`);
        return `⟦${key}⟧`;
      }
      if (language !== FALLBACK_LANGUAGE) reportOnce('warn', 'i18n', `"${key}" has no ${language} text: shown in ${FALLBACK_LANGUAGE}`);
    }
    return params ? fill(text, params) : text;
  };
}

function fill(text: string, params: Params): string {
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in params ? String(params[name]) : whole));
}

/** Money in the session's language: 59,9 → "59,90 €". */
export function formatMoney(amount: number, language: string, currency = 'EUR'): string {
  return new Intl.NumberFormat(locale(language), { style: 'currency', currency }).format(amount);
}

export function locale(language: string): string {
  return { it: 'it-IT', en: 'en-GB', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' }[language] ?? language;
}
